import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'

import Button from 'components/Form/Button'
import InviteFormModal from 'components/StaffEssentialsInvites/InviteFormModal'
import InviteHistoryModal from 'components/StaffEssentialsInvites/InviteHistoryModal'
import InvitesTable from 'components/StaffEssentialsInvites/InvitesTable'
import SummaryCards from 'components/StaffEssentialsInvites/SummaryCards'
import { StatusPill } from 'components/StaffEssentialsInvites/styled'
import { CoreContext } from 'context/CoreContext'
import useI18n from 'hooks/useI18n'
import {
  CreateEssentialsInvite,
  ReadEssentialsInviteHistory,
  ReadAllEssentialsContentCatalog,
  ReadEssentialsInvites,
  UpdateEssentialsInvite,
} from 'services/essentialsInvites'
import { applyContentPresets, toAllowedContentSelection } from 'components/StaffEssentialsInvites/contentPresets'
import { ReadCompanyOptions } from 'services/companies'
import { apiRequest, exposeStrapiError } from 'utils'
import { isStaffUser } from 'utils/staff'
import {
  HeaderActions,
  Page,
  PageEyebrow,
  PageHeader,
  PageIntro,
  PageSubtitle,
  PageTitle,
  Panel,
  PanelHeader,
  PanelMeta,
  PanelTitle,
} from './styled'
import { FormSpacer } from 'ui/styled'

const emptyForm = {
  title: '',
  invite_code: '',
  company_id: '',
  company_name: '',
  company_selection_changed: false,
  max_uses: 1,
  expires_at: '',
  access_duration_days: 30,
  is_active: true,
  content_access_mode: 'allowlist',
  content_selection_changed: false,
  allowed_contents: [],
  content_group_ids: [],
}

const toDateTimeInput = value => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return offsetDate.toISOString().slice(0, 16)
}

const buildPayload = form => ({
  title: form.title,
  invite_code: form.invite_code,
  company_name: form.company_name,
  ...(form.company_selection_changed ? { company_id: form.company_id } : {}),
  max_uses: form.max_uses === '' ? null : Number(form.max_uses),
  ...(form.access_duration_days != null
    ? { access_duration_days: Number(form.access_duration_days), expires_at: null }
    : { expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null }),
  is_active: form.is_active !== false,
  ...(form.content_selection_changed ? {
    content_access_mode: 'allowlist',
    allowed_contents: form.allowed_contents || [],
    content_group_ids: form.content_group_ids || [],
  } : {}),
})

export default function StaffEssentialsInvites() {
  const history = useHistory()
  const { user } = useContext(CoreContext)
  const { t } = useI18n()
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editingInvite, setEditingInvite] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [companyOptions, setCompanyOptions] = useState([])
  const [companyLoading, setCompanyLoading] = useState(false)
  const [companyQuery, setCompanyQuery] = useState('')
  const [historyInvite, setHistoryInvite] = useState(null)
  const [historyRows, setHistoryRows] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [contentCatalog, setContentCatalog] = useState([])
  const [contentCatalogTypes, setContentCatalogTypes] = useState([])
  const [contentCatalogLoading, setContentCatalogLoading] = useState(false)
  const [contentCatalogError, setContentCatalogError] = useState('')
  const [contentQuery, setContentQuery] = useState('')
  const [contentType, setContentType] = useState('')
  const [contentGroups, setContentGroups] = useState([])
  const [contentGroupsLoading, setContentGroupsLoading] = useState(false)
  const [contentGroupsError, setContentGroupsError] = useState('')

  const isStaff = isStaffUser(user)

  useEffect(() => {
    if (user?.id && !isStaff) {
      history.replace('/dashboard')
    }
  }, [history, isStaff, user?.id])

  const loadInvites = useCallback(async () => {
    if (!isStaff) return
    setLoading(true)
    const result = await apiRequest(() => ReadEssentialsInvites())
    if (result && !exposeStrapiError(result)) {
      setInvites(Array.isArray(result?.data) ? result.data : [])
    }
    setLoading(false)
  }, [isStaff])

  const loadCompanies = useCallback(async (query, selectedCompany) => {
    setCompanyLoading(true)
    const result = await apiRequest(() => ReadCompanyOptions({ query, page: 1, pageSize: 30 }))
    if (result && !exposeStrapiError(result)) {
      const options = Array.isArray(result?.data) ? result.data : []
      const selectedCompanyId = selectedCompany?.id
      const hasSelected = !selectedCompanyId || options.some(option => (
        String(option.id) === String(selectedCompanyId)
      ))
      setCompanyOptions(hasSelected || !selectedCompany?.name
        ? options
        : [{
          id: selectedCompanyId,
          name: selectedCompany.name,
        }, ...options])
    }
    setCompanyLoading(false)
  }, [])

  useEffect(() => {
    if (!formOpen) return undefined
    const selectedCompany = form.company_id ? {
      id: form.company_id,
      name: form.company_name,
    } : null
    const timeout = window.setTimeout(() => loadCompanies(companyQuery, selectedCompany), 300)
    return () => window.clearTimeout(timeout)
  }, [companyQuery, form.company_id, form.company_name, formOpen, loadCompanies])

  useEffect(() => {
    loadInvites()
  }, [loadInvites])

  useEffect(() => {
    if (!formOpen) return undefined
    let cancelled = false
    const loadContentCatalog = async () => {
      setContentCatalogLoading(true)
      setContentCatalogError('')
      setContentGroupsLoading(true)
      setContentGroupsError('')
      const result = await apiRequest(() => ReadAllEssentialsContentCatalog())
      if (cancelled) return
      if (result && !result?.error) {
        const catalog = Array.isArray(result?.data) ? result.data : []
        setContentCatalog(catalog)
        setContentCatalogTypes(Array.isArray(result?.meta?.types) ? result.meta.types : [])
        setContentGroups(Array.isArray(result?.meta?.contentGroups) ? result.meta.contentGroups : [])
        if (!editingInvite) {
          setForm(current => current.content_selection_changed ? current : {
            ...current,
            content_selection_changed: true,
            allowed_contents: toAllowedContentSelection(catalog),
          })
        }
      } else {
        setContentCatalog([])
        setContentCatalogError(t('staff_essentials_invites_locked_content_error'))
        setContentGroups([])
        setContentGroupsError(t('staff_essentials_invites_content_preset_error'))
      }
      setContentCatalogLoading(false)
      setContentGroupsLoading(false)
    }
    loadContentCatalog()
    return () => { cancelled = true }
  }, [editingInvite, formOpen, t])

  const filteredContentCatalog = useMemo(() => contentCatalog
    .filter(item => !contentType || item.type === contentType)
    .filter(item => !contentQuery.trim() || item.title.toLowerCase().includes(contentQuery.trim().toLowerCase())),
  [contentCatalog, contentQuery, contentType])

  const summary = useMemo(() => invites.reduce((acc, invite) => {
    acc.total += 1
    if (invite.is_active) acc.active += 1
    acc.used += Number(invite.uses || 0)
    if (invite.remaining_uses != null) acc.remaining += Number(invite.remaining_uses || 0)
    return acc
  }, { total: 0, active: 0, used: 0, remaining: 0 }), [invites])

  const openCreate = useCallback(() => {
    setContentCatalog([])
    setContentCatalogLoading(true)
    setContentGroups([])
    setContentGroupsLoading(true)
    setEditingInvite(null)
    setForm(emptyForm)
    setCompanyOptions([])
    setCompanyQuery('')
    setContentQuery('')
    setContentType('')
    setFormOpen(true)
  }, [])

  const closeForm = useCallback(() => {
    setFormOpen(false)
  }, [])

  const openEdit = useCallback(invite => {
    const selectedCompany = invite.company?.id ? {
      id: invite.company.id,
      name: invite.company.name || invite.company_name,
    } : null

    setContentCatalog([])
    setContentCatalogLoading(true)
    setContentGroups([])
    setContentGroupsLoading(true)
    setEditingInvite(invite)
    setForm({
      title: invite.title || '',
      invite_code: invite.invite_code || '',
      company_id: selectedCompany?.id || '',
      company_name: invite.company_name || '',
      company_selection_changed: false,
      max_uses: invite.max_uses == null ? '' : invite.max_uses,
      expires_at: toDateTimeInput(invite.expires_at),
      access_duration_days: invite.access_duration_days ?? null,
      is_active: invite.is_active !== false,
      content_access_mode: invite.content_access_mode || 'denylist',
      content_selection_changed: false,
      allowed_contents: Array.isArray(invite.allowed_contents) ? invite.allowed_contents : [],
      content_group_ids: Array.isArray(invite.content_group_ids) ? invite.content_group_ids : [],
    })
    setCompanyOptions(selectedCompany ? [selectedCompany] : [])
    setCompanyQuery(selectedCompany?.name || '')
    setContentQuery('')
    setContentType('')
    setFormOpen(true)
  }, [])

  const handleContentGroupChange = useCallback(groupIds => {
    setForm(current => applyContentPresets(current, contentGroups.filter(item => (
      groupIds.includes(String(item.documentId || item.id))
    ))))
  }, [contentGroups])

  const saveInvite = async (values = form) => {
    if (!String(values.title || '').trim()) {
      toast.error(t('staff_essentials_invites_title_required'))
      return
    }
    if (!String(values.invite_code || '').trim()) {
      toast.error(t('staff_essentials_invites_code_required') || 'Invite code is required.')
      return
    }
    setSaving(true)
    const payload = buildPayload(values)
    const result = editingInvite
      ? await apiRequest(() => UpdateEssentialsInvite(editingInvite.documentId || editingInvite.id, payload))
      : await apiRequest(() => CreateEssentialsInvite(payload))

    if (result && !exposeStrapiError(result)) {
      toast.success(t(editingInvite ? 'staff_essentials_invites_updated' : 'staff_essentials_invites_created'))
      setFormOpen(false)
      await loadInvites()
    }
    setSaving(false)
  }

  const copyLink = useCallback(async invite => {
    const value = invite?.link
    if (!value) {
      toast.error(t('staff_essentials_invites_link_unavailable'))
      return
    }

    try {
      await navigator.clipboard.writeText(value)
      toast.success(t('staff_essentials_invites_link_copied'))
    } catch {
      window.prompt(t('staff_essentials_invites_copy_prompt'), value)
    }
  }, [t])

  const openHistory = useCallback(async invite => {
    setHistoryInvite(invite)
    setHistoryRows([])
    setHistoryLoading(true)
    const result = await apiRequest(() => ReadEssentialsInviteHistory(invite.documentId || invite.id))
    if (result && !exposeStrapiError(result)) {
      setHistoryRows(Array.isArray(result?.data?.users) ? result.data.users : [])
    }
    setHistoryLoading(false)
  }, [])

  if (!isStaff) return null

  return (
    <>
      <FormSpacer extraLarge />

      <Page>
        <PageHeader>
          <PageIntro>
            <PageEyebrow>{t('staff_essentials_invites_eyebrow')}</PageEyebrow>
            <PageTitle>{t('staff_essentials_invites_title')}</PageTitle>
            <PageSubtitle>{t('staff_essentials_invites_subtitle')}</PageSubtitle>
          </PageIntro>
          <HeaderActions>
            <Button outline color="blue" width="fit-content" startIcon={<RefreshIcon />} onClick={loadInvites} disabled={loading}>
              {t('staff_essentials_invites_refresh')}
            </Button>
            <Button color="blue" width="fit-content" startIcon={<AddIcon />} onClick={openCreate}>
              {t('staff_essentials_invites_create')}
            </Button>
          </HeaderActions>
        </PageHeader>

        <SummaryCards summary={summary} />

        <Panel>
          <PanelHeader>
            <div>
              <PanelTitle>{t('staff_essentials_invites_links')}</PanelTitle>
              <PanelMeta>
                {invites.length
                  ? `${invites.length} ${t(invites.length === 1 ? 'staff_essentials_invites_found_singular' : 'staff_essentials_invites_found_plural')}`
                  : t('staff_essentials_invites_none_created')}
              </PanelMeta>
            </div>
            <StatusPill active>{t(loading ? 'staff_essentials_invites_syncing' : 'staff_essentials_invites_live_data')}</StatusPill>
          </PanelHeader>

          <InvitesTable
            invites={invites}
            loading={loading}
            onCreate={openCreate}
            onCopy={copyLink}
            onEdit={openEdit}
            onHistory={openHistory}
          />
        </Panel>

        {formOpen ? (
          <InviteFormModal
            editingInvite={editingInvite}
            form={form}
            saving={saving}
            companyOptions={companyOptions}
            companyLoading={companyLoading}
            companyQuery={companyQuery}
            contentGroups={contentGroups}
            contentGroupsLoading={contentGroupsLoading}
            contentGroupsError={contentGroupsError}
            selectedContentGroupId={form.content_group_ids || []}
            contentCatalog={filteredContentCatalog}
            contentCatalogTypes={contentCatalogTypes}
            contentCatalogLoading={contentCatalogLoading}
            contentCatalogError={contentCatalogError}
            contentQuery={contentQuery}
            contentType={contentType}
            setContentQuery={setContentQuery}
            setContentType={setContentType}
            setCompanyQuery={setCompanyQuery}
            setForm={setForm}
            onContentGroupChange={handleContentGroupChange}
            onClose={closeForm}
            onSave={saveInvite}
          />
        ) : null}

        {historyInvite ? (
          <InviteHistoryModal
            invite={historyInvite}
            rows={historyRows}
            loading={historyLoading}
            onClose={() => setHistoryInvite(null)}
          />
        ) : null}
      </Page>
    </>
  )
}
